/**
 * Buscar por foto sin salir de la pagina (BUS-03, BUS-04, BUS-05).
 *
 * Es un panel colgado de la caja de busqueda, como el de sugerencias, y no una
 * pantalla aparte (rediseño del 14-09-2026, con el buscador por foto de
 * lowes.com como referencia): quien esta mirando una categoria y saca la foto
 * de la pieza no tiene por que irse a otra vista para eso. Pasa por tres
 * momentos, todos aca adentro: elegir la foto, recortarla y ver los parecidos.
 *
 * TRES DECISIONES QUE SE VEN EN EL PANEL:
 *
 * 1. Camara y galeria son DOS inputs en pantallas tactiles. `capture` obliga a
 *    la camara y le saca la galeria a quien ya tenia la foto sacada, asi que
 *    el de la galeria no lo lleva. Pero sin `capture` solo iOS ofrece la
 *    camara en su menu: Android 14+ abre el selector de fotos del sistema, que
 *    no la tiene. Por eso el tactil muestra los dos botones (BUS-04); con
 *    mouse no hay camara que abrir y queda solo el de la galeria.
 * 2. Los resultados se llaman "parecidos", no "resultados". El primero acierta
 *    el producto exacto en tres de cada cuatro casos; presentarlos como LA
 *    respuesta seria mentirle al cuarto comprador.
 * 3. Por lo mismo, WhatsApp esta SIEMPRE, tambien cuando hay parecidos. En la
 *    busqueda por texto aparece solo si no hubo nada (BUS-06); aca el caso malo
 *    no es la lista vacia, es la lista llena en la que no esta lo que buscaba.
 *
 * La foto no se guarda en ningun lado: vive en memoria mientras el panel esta
 * abierto, se sube recortada y reducida, el servidor la compara y la descarta.
 */
import { useEffect, useState, type ChangeEvent, type DragEvent } from 'react';

import type { ProductoResumen } from '@elcarpintero/compartido';

import { useConsultaMedios } from '../../hooks/use-consulta-medios';
import { ErrorDeApi } from '../../servicios/cliente-api';
import { buscarPorFoto } from '../../servicios/busqueda.servicio';
import { registrarClicWhatsapp } from '../../servicios/metricas.servicio';
import {
  ErrorDeFoto,
  RECORTE_COMPLETO,
  leerFoto,
  liberarFoto,
  recortarFoto,
  type FotoLeida,
  type Recorte,
} from '../../utilidades/foto-busqueda';
import { whatsappBusquedaPorFoto } from '../../utilidades/whatsapp';
import { GrillaProductos } from '../catalogo/GrillaProductos';
import { Cargando } from '../comunes/EstadoPeticion';
import { Icono } from '../comunes/Icono';
import { RecortadorFoto } from './RecortadorFoto';

type Fase =
  | { nombre: 'elegir' }
  | { nombre: 'leyendo' }
  | { nombre: 'recortar' }
  | { nombre: 'buscando' }
  | { nombre: 'listo'; parecidos: ProductoResumen[]; miniatura: string }
  | { nombre: 'error'; mensaje: string };

export interface PanelBusquedaFotoProps {
  /** Id del panel, para el `aria-controls` del boton que lo abre. */
  id: string;
  onCerrar(): void;
}

function mensajeDe(error: unknown): string {
  if (error instanceof ErrorDeFoto || error instanceof ErrorDeApi) return error.message;
  return 'No pudimos buscar con esa foto. Intenta de nuevo.';
}

export function PanelBusquedaFoto({ id, onCerrar }: PanelBusquedaFotoProps) {
  const [fase, setFase] = useState<Fase>({ nombre: 'elegir' });
  const [foto, setFoto] = useState<FotoLeida | null>(null);
  const [recorte, setRecorte] = useState<Recorte>(RECORTE_COMPLETO);
  const [arrastrando, setArrastrando] = useState(false);
  const tactil = useConsultaMedios('(pointer:coarse)');

  // La foto y la miniatura son blobs del navegador: se liberan al cambiarlos y
  // al cerrar el panel, o quedan retenidos hasta que se cierra la pestaña.
  useEffect(() => {
    if (foto === null) return;
    return () => liberarFoto(foto);
  }, [foto]);
  const miniatura = fase.nombre === 'listo' ? fase.miniatura : null;
  useEffect(() => {
    if (miniatura === null) return;
    return () => URL.revokeObjectURL(miniatura);
  }, [miniatura]);

  async function elegir(archivo: File | undefined): Promise<void> {
    if (archivo === undefined) return;

    setFase({ nombre: 'leyendo' });
    try {
      setFoto(await leerFoto(archivo));
      setRecorte(RECORTE_COMPLETO);
      setFase({ nombre: 'recortar' });
    } catch (error) {
      setFase({ nombre: 'error', mensaje: mensajeDe(error) });
    }
  }

  async function buscar(): Promise<void> {
    if (foto === null) return;

    setFase({ nombre: 'buscando' });
    try {
      const pedazo = await recortarFoto(foto, recorte);
      const resultado = await buscarPorFoto(pedazo);
      setFase({
        nombre: 'listo',
        parecidos: resultado.productos,
        miniatura: URL.createObjectURL(pedazo),
      });
    } catch (error) {
      setFase({ nombre: 'error', mensaje: mensajeDe(error) });
    }
  }

  function alElegirArchivo(evento: ChangeEvent<HTMLInputElement>): void {
    void elegir(evento.target.files?.[0]);
    // Permite volver a elegir la MISMA foto: sin esto el `change` no se
    // dispara la segunda vez.
    evento.target.value = '';
  }

  function soltarArchivo(evento: DragEvent<HTMLDivElement>): void {
    evento.preventDefault();
    setArrastrando(false);
    void elegir(evento.dataTransfer.files[0]);
  }

  // En tactil vuelve a elegir, donde estan camara y galeria; con mouse abre
  // directo el selector de archivos, que es la unica opcion.
  function otraFoto(clase: string) {
    return tactil ? (
      <button type="button" className={clase} onClick={() => setFase({ nombre: 'elegir' })}>
        Otra foto
      </button>
    ) : (
      <label className={clase} htmlFor={`${id}-archivo`}>
        Otra foto
      </label>
    );
  }

  const ocupado = fase.nombre === 'leyendo' || fase.nombre === 'buscando';
  const conFoto = foto !== null && (fase.nombre === 'recortar' || fase.nombre === 'buscando');

  return (
    <div className="panel-foto" id={id} role="region" aria-label="Buscar por foto">
      <header className="panel-foto-cab">
        <div>
          <p className="t">
            {fase.nombre === 'listo'
              ? 'Parecidos a tu foto'
              : conFoto
                ? 'Recorta lo que quieres buscar'
                : 'Buscar por foto'}
          </p>
          <p className="d">
            {fase.nombre === 'listo'
              ? 'Buscar por foto acierta la mayoría de las veces, pero no todas.'
              : conFoto
                ? 'Deja dentro del marco solo la pieza: el fondo confunde al buscador.'
                : 'La foto no se guarda: se usa solo para esta búsqueda.'}
          </p>
        </div>
        <button type="button" className="modal-cerrar" onClick={onCerrar} aria-label="Cerrar">
          <Icono nombre="cerrar" />
        </button>
      </header>

      {/*
        Inputs `type="file"` de verdad, escondidos detras de los botones: es
        lo unico que abre la camara y la galeria nativas. Solo el de la camara
        lleva `capture` (ver el comentario de arriba).
      */}
      <input
        id={`${id}-archivo`}
        className="solo-lectores"
        type="file"
        accept="image/*"
        disabled={ocupado}
        onChange={alElegirArchivo}
      />
      {tactil ? (
        <input
          id={`${id}-camara`}
          className="solo-lectores"
          type="file"
          accept="image/*"
          capture="environment"
          disabled={ocupado}
          onChange={alElegirArchivo}
        />
      ) : null}

      {fase.nombre === 'elegir' || fase.nombre === 'leyendo' || fase.nombre === 'error' ? (
        <div
          className={arrastrando ? 'panel-foto-zona arrastrando' : 'panel-foto-zona'}
          onDragOver={(evento) => {
            evento.preventDefault();
            setArrastrando(true);
          }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={soltarArchivo}
        >
          {tactil ? (
            <div className="panel-foto-origenes">
              <label className="btn btn-primario" htmlFor={`${id}-camara`}>
                <Icono nombre="camara" />
                Sacar una foto
              </label>
              <label className="btn btn-contorno" htmlFor={`${id}-archivo`}>
                <Icono nombre="galeria" />
                Elegir de la galeria
              </label>
            </div>
          ) : (
            <>
              <Icono nombre="galeria" />
              <p>
                Arrastra una foto hasta aca, o{' '}
                <label className="panel-foto-enlace" htmlFor={`${id}-archivo`}>
                  elige una de tu computador
                </label>
              </p>
            </>
          )}
          <p className="panel-foto-nota">Solo imagenes de hasta 40 MB.</p>
          {fase.nombre === 'leyendo' ? <Cargando que="la foto" /> : null}
          {fase.nombre === 'error' ? (
            <p className="foto-busqueda-error" role="alert">
              <Icono nombre="alerta" />
              {fase.mensaje}
            </p>
          ) : null}
        </div>
      ) : null}

      {conFoto && foto !== null ? (
        <>
          <RecortadorFoto
            src={foto.url}
            ancho={foto.ancho}
            alto={foto.alto}
            recorte={recorte}
            onChange={setRecorte}
          />
          <div className="panel-foto-acciones">
            {otraFoto('btn btn-contorno')}
            <button
              type="button"
              className="btn btn-primario"
              disabled={ocupado}
              onClick={() => {
                void buscar();
              }}
            >
              <Icono nombre="buscar" />
              {fase.nombre === 'buscando' ? 'Buscando…' : 'Buscar parecidos'}
            </button>
          </div>
          {fase.nombre === 'buscando' ? <Cargando que="los parecidos" /> : null}
        </>
      ) : null}

      {fase.nombre === 'listo' ? (
        <>
          <div className="panel-foto-consulta">
            <img src={fase.miniatura} alt="El recorte con el que buscaste" />
            <div className="panel-foto-acciones">
              <button
                type="button"
                className="btn btn-sm btn-contorno"
                onClick={() => setFase({ nombre: 'recortar' })}
              >
                Ajustar el recorte
              </button>
              {otraFoto('btn btn-sm btn-contorno')}
            </div>
          </div>

          {/* Sin `titula` y con rotulos en vez de encabezados: el panel vive
              en la cabecera, antes del h1 de la pagina, y un h2 ahi rompe la
              jerarquia de la vista (mismo criterio que el menu del catalogo). */}
          {fase.parecidos.length > 0 ? (
            <GrillaProductos productos={fase.parecidos} titulo="Productos parecidos a tu foto" />
          ) : null}

          <section className="rescate" aria-labelledby={`${id}-ninguno`}>
            <p className="t" id={`${id}-ninguno`}>
              <Icono nombre="lupaVacia" />
              {fase.parecidos.length > 0
                ? '¿Ninguno es el que buscas?'
                : 'No encontramos nada parecido'}
            </p>
            <p>Mándanos la foto por WhatsApp y te decimos exactamente cuál es y si lo tenemos.</p>
            <p className="acc">
              <a
                className="btn btn-wa"
                href={whatsappBusquedaPorFoto()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => registrarClicWhatsapp({ origen: 'busqueda' })}
              >
                <Icono nombre="wa" />
                Mandar la foto por WhatsApp
              </a>
            </p>
          </section>
        </>
      ) : null}
    </div>
  );
}
